import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { LiveRegion } from '@/components/ui/LiveRegion';
import { IMAGE_TYPES, TYPES_IN_WORDS, inMegabytes, refuseFile } from '@/lib/images';

interface ImageUploadProps {
  /** What this image is, in words: "Organization logo", "Cover image". */
  label: string;
  /** The alt text for the image itself, which is the same thing said again. */
  alt: string;
  /** A viewable address, or null when there is no image yet. */
  src?: string | null;
  maxBytes: number;
  disabled?: boolean;
  /** Sends the file. Rejecting shows the reason next to the image. */
  onUpload: (file: File) => Promise<unknown>;
  /** Removes the stored image. Asking first is the caller's job. */
  onRemove: () => void;
}

/**
 * One image: what is stored now, a way to replace it, and a way to remove it.
 *
 * The file is sent the moment it is chosen rather than on Save. A picture is
 * large and the rest of the form is not, so a slow or failed upload should be
 * reported next to the image instead of turning into a failed save of the
 * description somebody also typed. It also means the form never carries a
 * storage key, which is the backend's to set.
 *
 * Shared, so the ARC header image can use it unchanged.
 */
export function ImageUpload({
  label,
  alt,
  src,
  maxBytes,
  disabled,
  onUpload,
  onRemove,
}: ImageUploadProps) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * An object URL holds its file in memory until it is revoked. Replacing the
   * preview or leaving the page has to let the old one go, or a person who
   * tries four logos has four images held for as long as the tab is open.
   */
  const revoke = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (revoke.current) URL.revokeObjectURL(revoke.current);
    },
    [],
  );

  function showPreview(file: File) {
    if (revoke.current) URL.revokeObjectURL(revoke.current);
    const url = URL.createObjectURL(file);
    revoke.current = url;
    setPreview(url);
  }

  async function choose(file: File) {
    const refusal = refuseFile(file, maxBytes);
    if (refusal) {
      // Nothing is sent and nothing is previewed: the file is not going to
      // become this image, so showing it as though it might is a lie.
      setError(refusal);
      return;
    }

    setError(null);
    showPreview(file);
    setBusy(true);
    try {
      await onUpload(file);
    } catch (cause) {
      // The stored image is still whatever it was, so the preview goes and the
      // old picture comes back with the reason beside it.
      if (revoke.current) URL.revokeObjectURL(revoke.current);
      revoke.current = null;
      setPreview(null);
      setError(cause instanceof Error ? cause.message : 'That image could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  const shown = preview ?? src ?? null;

  return (
    <Box
      role="group"
      aria-label={label}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          bgcolor: 'action.hover',
        }}
      >
        {shown ? (
          <Box
            component="img"
            src={shown}
            alt={alt}
            sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }}
          />
        ) : (
          /* Text, not a grey box. A placeholder nobody can read is invisible
             to a screen reader and meaningless to everybody else. */
          <Typography variant="body2" color="text.secondary">
            No {label.toLowerCase()} yet
          </Typography>
        )}
      </Paper>

      {busy && <LinearProgress aria-hidden />}
      <LiveRegion message={busy ? `Sending the ${label.toLowerCase()}` : ''} />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || busy}
          onClick={() => input.current?.click()}
        >
          {shown ? `Replace ${label.toLowerCase()}` : `Choose ${label.toLowerCase()}`}
        </Button>

        {src && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || busy}
            onClick={onRemove}
          >
            Remove {label.toLowerCase()}
          </Button>
        )}
      </Box>

      {/* Hidden because a bare file input cannot be styled and reads as
          "No file chosen" in most browsers. The button above is its label. */}
      <input
        ref={input}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        hidden
        aria-hidden
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so that choosing the same file twice in a row still fires
          // a change event, which is what a person retrying after a failure does.
          event.target.value = '';
          if (file) void choose(file);
        }}
      />

      <Typography variant="caption" color="text.secondary">
        {TYPES_IN_WORDS}, up to {inMegabytes(maxBytes)}.
      </Typography>
    </Box>
  );
}
